import { clsx } from '@alexis/helpers/clsx';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import FareDetail from '@components/flights/Fare/FareDetail';
import FarePromos from '@components/flights/Fare/FarePromos';
import Price from '@components/Price';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';
import { recommendationTypeTranslationMap } from '@constants/flight';

import { computeFeeTextForFare, hasBnplPaymentOptionForFareDisplay } from '@helpers/flight';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';

import InfoIcon from '@icons/info.svg';

import {
  getCurrencyState,
  getIsRtlState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
} from '@redux/selectors';

import type { FlightFareResult } from '@wegoTypes/flights/metasearch/flightFareResult';

import styles from './NormalFare.module.scss';

interface NormalFareProps {
  fare: FlightFareResultTripFare;
  fareIndex: number;
  fareUrl: string;
  filteredFares: Array<FlightFareResultTripFare>;
  flightFareResult: FlightFareResult;
  isLoadingFares: boolean;
  isSpecialFlight: boolean;
  passengerCount: number;
  paymentMethodsFromApi: Array<HomepageFlightsPaymentMethod>;
  onViewDealClick: (
    e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>,
    fare: FlightFareResultTripFare,
    isSpecialFlightFare: boolean,
  ) => void;
}

const NormalFare: React.FC<NormalFareProps> = ({
  fare,
  fareIndex,
  fareUrl,
  filteredFares,
  flightFareResult,
  isLoadingFares,
  isSpecialFlight,
  passengerCount,
  paymentMethodsFromApi,
  onViewDealClick,
}) => {
  const currency = useSelector(getCurrencyState);
  const isRtl = useSelector(getIsRtlState);
  const locale = useSelector(getLocaleState);
  const priceOptions = useSelector(getPriceOptionsState);
  const translations = useSelector(getTranslationsState);

  const { exchangeRate } = useFlightSearchResults();

  const [isFareExpanded, setIsFareExpanded] = useState(false);

  const isAirlineBackfillFare = fare.legsSourceTypes?.includes('AIRLINE_BACKFILL');

  useEffect(() => {
    setIsFareExpanded(false);
  }, [filteredFares]);

  const handleFareDetailToggle = useCallback(() => {
    setIsFareExpanded((prev) => !prev);
  }, []);

  const FareCTAButton = useMemo(() => {
    if (fare.provider.type === 'airline') {
      return (
        <div
          data-pw='tripFare_viewDealBtn'
          className={clsx(styles.button, styles.bookInstant)}
          onClick={(e) => onViewDealClick(e, fare, isSpecialFlight)}
        >
          {translations.Select}
        </div>
      );
    }

    if (fare.provider.wegoFare) {
      return (
        <a
          data-pw='tripFare_viewDealBtn'
          className={clsx(styles.button, styles.wegoFare)}
          onClick={(e) => onViewDealClick(e, fare, isSpecialFlight)}
          href={fareUrl}
          rel='noreferrer'
          target='_blank'
        >
          {translations.Select}
        </a>
      );
    }

    return (
      <div
        data-pw='tripFare_viewDealBtn'
        className={clsx(styles.button, styles.ota)}
        onClick={(e) => onViewDealClick(e, fare, isSpecialFlight)}
      >
        {translations.Select}
      </div>
    );
  }, [fare, fareUrl, isSpecialFlight, onViewDealClick, translations.Select]);

  return (
    <div
      className={clsx(
        styles.container,
        fareIndex === 0 && styles.firstFare,
        fareIndex === filteredFares.length - 1 && filteredFares.length <= 10 && styles.lastFare,
        isLoadingFares && styles.isLoading,
        !isFareExpanded && styles.isNotExpanded,
        isAirlineBackfillFare && styles.airlineBackfillFare,
        isRtl && styles.rtl,
      )}
    >
      <div
        data-pw='tripFares_fareContainer'
        className={styles.providerDetailsAndPromos}
        onClick={handleFareDetailToggle}
      >
        <div className={styles.providerDetailsContainer}>
          <div className={styles.providerDetails}>
            <div data-pw='tripFare_providerLogo' className={styles.providerLogoContainer}>
              <img
                className={styles.providerLogo}
                src={`https://res.cloudinary.com/wego/image/upload/c_fit,w_200,h_70/${CLOUDINARY_DAILY_KEY}/providers/rectangular_logos/${fare.provider.code}.png`}
              />
            </div>

            <div
              className={clsx(styles.providerPrice, fare.paymentFees.length > 0 && styles.hasFees)}
            >
              <div className={styles.priceContainer}>
                {isAirlineBackfillFare && (
                  <div className={styles.fromText}>{translations.lbl_from}</div>
                )}
                <Price
                  dataPw='tripFare_price'
                  className={styles.price}
                  price={convertFlightSearchResultFarePriceToPrice(
                    fare.price,
                    passengerCount,
                    priceOptions.showTotalPrice,
                    true,
                  )}
                  locale={locale}
                  currency={currency}
                  exchangeRate={exchangeRate}
                />
              </div>
            </div>

            <div
              className={clsx(
                styles.paymentFeesContainer,
                fare.paymentFees.length > 0 && styles.hasFees,
              )}
            >
              <div data-pw='tripFare_paymentFeeNotice' className={styles.paymentFees}>
                {computeFeeTextForFare(
                  fare,
                  currency,
                  flightFareResult.paymentMethods,
                  priceOptions,
                  translations,
                  locale,
                )}

                <div className={clsx(styles.chevron, styles.bottom)}></div>
              </div>
            </div>
          </div>

          {FareCTAButton}
        </div>

        {hasBnplPaymentOptionForFareDisplay(paymentMethodsFromApi, fare) ||
        fare.sponsoredListing ||
        fare.recommendationType ? (
          <div className={styles.tagsContainer}>
            {fare.sponsoredListing && (
              <div className={clsx(styles.tag, styles.sponsored)}>
                {translations.flights_inline_ads_sponsored_tag}
              </div>
            )}

            {fare.recommendationType && (
              <div className={clsx(styles.tag, styles.recommendationType)}>
                {translations[recommendationTypeTranslationMap[fare.recommendationType]]}
              </div>
            )}

            {hasBnplPaymentOptionForFareDisplay(paymentMethodsFromApi, fare) ? (
              <div className={clsx(styles.tag, styles.bnpl)}>
                <img
                  src={cloudinaryImageTransformations(
                    'https://assets.wego.com/image/upload/v1663418328/web/payment_cards/tabby_square_3x.png',
                    'c_fill',
                    'fl_lossy',
                    'q_auto:low',
                    'f_auto',
                    'w_48',
                    'h_48',
                  )}
                />

                {translations.lbl_interest_free_payments}

                <div className={styles.tooltipContainer}>
                  <InfoIcon className={styles.icon} />

                  <div className={clsx(styles.tooltip, styles.bottom)}>
                    <span className={styles.bnplTooltipHeader}>{translations.how_it_works}</span>

                    <ol>
                      <li>
                        <div className={styles.flexBox}>
                          <span>{translations.choose}</span>

                          <img src='https://assets.wego.com/image/upload/v1675920776/payment_logos/3_interest-free_payments.png' />

                          <span>{translations.at_checkout}</span>
                        </div>
                      </li>

                      <li>{translations.redirect_to_tabby}</li>

                      <li>
                        <div className={clsx(styles.flexBox, styles.payWith)}>
                          {translations.choose_pay_with}

                          <img src='https://assets.wego.com/image/upload/v1495167459/payment_logos/VISA_CREDIT_CARD.png' />

                          <img src='https://assets.wego.com/image/upload/v1495167459/payment_logos/MASTERCARD_CREDIT_CARD.png' />

                          <img src='https://assets.wego.com/image/upload/v1660734745/payment_logos/apple_pay.png' />
                        </div>
                      </li>

                      <li>{translations.tabby_sms_reminder}</li>
                    </ol>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <FarePromos
          isWegoFare={fare.provider.wegoFare}
          isLoadingFares={isLoadingFares}
          promos={fare.promos}
        />
      </div>

      {isFareExpanded && (
        <FareDetail
          fare={fare}
          flightFareResult={flightFareResult}
          isLastFare={fareIndex === filteredFares.length - 1}
          passengerCount={passengerCount}
          onHide={() => setIsFareExpanded(false)}
        />
      )}
    </div>
  );
};

export default NormalFare;
